if (!customElements.get('warehouse-stock')) {
  customElements.define(
    'warehouse-stock',
    class WarehouseStock extends HTMLElement {
      constructor() {
        super();
        this.body = this.querySelector('[data-wh-body]');
        this.modal = this.querySelector('[data-wh-modal]');
        this.panel = this.querySelector('[data-wh-panel]');
        this.trigger = this.querySelector('[data-wh-trigger]');
        this.overlay = this.querySelector('[data-wh-overlay]');
        this.closeButton = this.querySelector('[data-wh-close]');
        this.triggerLabel = this.querySelector('[data-wh-trigger-label]');
        this.titleEl = this.querySelector('[data-wh-title]');
        this.closeLabel = this.querySelector('[data-wh-close-label]');
        this.keyMap = this.parseJSON('[data-variant-keys]');
        this.strings = this.parseJSON('[data-strings]');

        this.token = 0;
        this.controller = null;
        this.isOpen = false;
        this.loadedKey = null;
        this.designMode = this.dataset.designMode === '1';
        this.cacheMinutes = parseInt(this.dataset.cacheMinutes, 10) || 60;
        this.lowThreshold = parseInt(this.dataset.lowThreshold, 10) || 3;
      }

      connectedCallback() {
        if (this.triggerLabel) this.triggerLabel.textContent = this.strings.show_button || '';
        if (this.titleEl) this.titleEl.textContent = this.strings.heading || '';
        if (this.closeLabel) this.closeLabel.textContent = this.strings.close || '';

        this.onTriggerClick = () => this.open();
        this.onCloseClick = () => this.close();
        this.onOverlayClick = () => this.close();

        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            event.stopPropagation();
            this.close();
          }
        };

        this.onModalClick = (event) => {
          if (!this.panel.contains(event.target)) this.close();
        };

        if (this.trigger) this.trigger.addEventListener('click', this.onTriggerClick);

        if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
        if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
        if (this.modal) this.modal.addEventListener('click', this.onModalClick);

        document.addEventListener('keydown', this.onKeydown);

        this.onVariantChange = (event) => {
          const data = event.data || {};
          if (data.sectionId !== this.dataset.sectionId) return;
          if (!data.variant) return;

          this.dataset.variantId = data.variant.id;
          this.syncTrigger();

          if (this.isOpen) this.load();
        };

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);

        this.syncTrigger();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.controller) this.controller.abort();
        document.removeEventListener('keydown', this.onKeydown);
        if (this.isOpen) this.releaseBody();
        if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      }

      parseJSON(selector) {
        const node = this.querySelector(selector);
        if (!node) return {};
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('warehouse-stock: could not parse', selector, error);
          return {};
        }
      }

      currentKey() {
        return (this.keyMap[this.dataset.variantId] || '').trim();
      }

      syncTrigger() {
        if (!this.trigger) return;
        const hasKey = this.currentKey().length > 0;
        this.trigger.hidden = !hasKey;
        if (!hasKey && this.isOpen) this.close();
      }

      open() {
        if (this.isOpen) return;

        this.isOpen = true;

        if (this.modal.parentElement !== document.body) document.body.appendChild(this.modal);

        this.modal.hidden = false;
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

        document.body.classList.add('overflow-hidden');

        this.load();

        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.closeButton || this.panel);
        }
      }

      close() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.modal.hidden = true;
        if (this.modal.parentElement === document.body) this.appendChild(this.modal);
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');

        this.releaseBody();

        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }

        if (this.trigger && !this.trigger.hidden) this.trigger.focus();
      }

      releaseBody() {
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }

      cacheKey(key) {
        return `optimo:v1:${key}`;
      }

      readSession(key) {
        if (this.designMode) return null;
        try {
          const raw = sessionStorage.getItem(this.cacheKey(key));
          if (!raw) return null;
          const entry = JSON.parse(raw);
          if (Date.now() - entry.ts > this.cacheMinutes * 60000) {
            sessionStorage.removeItem(this.cacheKey(key));
            return null;
          }
          return entry.payload;
        } catch (error) {
          return null;
        }
      }

      writeSession(key, payload) {
        if (this.designMode) return;
        try {
          sessionStorage.setItem(this.cacheKey(key), JSON.stringify({ ts: Date.now(), payload }));
        } catch (error) {
        }
      }

      async load() {
        const key = this.currentKey();
        if (!key) return this.renderEmpty();

        const cached = this.readSession(key);
        if (cached) {
          this.loadedKey = key;
          return this.renderPayload(cached);
        }

        const myToken = ++this.token;
        this.renderLoading();

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const timeout = setTimeout(() => this.controller.abort(), 8000);

        try {
          const url =
            `${this.dataset.endpoint}?key=${encodeURIComponent(key)}` +
            `&locale=${encodeURIComponent(this.dataset.locale || '')}` +
            `&hide_zero=${this.dataset.hideZero === '1' ? '1' : '0'}`;

          const response = await fetch(url, {
            signal: this.controller.signal,
            headers: { Accept: 'application/json' },
          });

          if (myToken !== this.token) return;

          if (response.status === 404) {
            const payload = { ok: false, error: 'not_found' };
            this.writeSession(key, payload);
            this.loadedKey = key;
            return this.renderPayload(payload);
          }

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const payload = await response.json();
          if (myToken !== this.token) return;

          this.writeSession(key, payload);
          this.loadedKey = key;
          this.renderPayload(payload);
        } catch (error) {
          if (myToken !== this.token || error.name === 'AbortError') return;
          this.renderError();
        } finally {
          clearTimeout(timeout);
        }
      }

      renderEmpty() {
        this.body.innerHTML = '';
        this.removeAttribute('data-state');
      }

      renderLoading() {
        this.setAttribute('data-state', 'loading');
        this.body.innerHTML =
          `<div class="warehouse-stock__spinner-wrap">` +
          `<svg class="warehouse-stock__spinner" viewBox="0 0 50 50" aria-hidden="true" focusable="false">` +
          `<circle class="warehouse-stock__spinner-track" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>` +
          `<circle class="warehouse-stock__spinner-arc" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>` +
          `</svg>` +
          `<p class="warehouse-stock__loading">${this.escape(this.strings.loading || '')}</p>` +
          `</div>`;
      }

      renderError() {
        this.setAttribute('data-state', 'error');
        this.body.innerHTML =
          `<p class="warehouse-stock__message">${this.escape(this.strings.unavailable || '')} ` +
          `<button type="button" class="warehouse-stock__retry link link--text underlined-link">` +
          `${this.escape(this.strings.refresh || '')}</button></p>`;

        const retry = this.body.querySelector('.warehouse-stock__retry');
        if (retry) retry.addEventListener('click', () => this.load());
      }

      renderPayload(payload) {
        if (!payload || payload.ok !== true || !payload.data) {
          this.setAttribute('data-state', 'unknown');
          this.body.innerHTML =
            `<p class="warehouse-stock__message">${this.escape(this.strings.not_tracked || '')}</p>`;
          return;
        }

        const data = payload.data;
        const warehouses = Array.isArray(data.warehouses) ? data.warehouses : [];

        if (warehouses.length === 0) {
          this.setAttribute('data-state', 'out');
          this.body.innerHTML =
            `<p class="warehouse-stock__message">` +
            `<span class="warehouse-stock__dot warehouse-stock__dot--out"></span>` +
            `${this.escape(this.strings.out_of_stock || '')}</p>`;
          return;
        }

        this.setAttribute('data-state', 'ready');

        const items = warehouses
          .map((warehouse) => {
            const quantity = Number(warehouse.quantity) || 0;
            const level = quantity <= 0 ? 'out' : quantity <= this.lowThreshold ? 'low' : 'in';
            const dot =
              this.dataset.displayMode === 'available' ? (quantity > 0 ? 'in' : 'out') : level;
            return (
              `<li class="warehouse-stock__item">` +
              `<span class="warehouse-stock__dot warehouse-stock__dot--${dot}" aria-hidden="true"></span>` +
              `<span class="warehouse-stock__name">${this.escape(warehouse.name || '')}</span>` +
              `<span class="warehouse-stock__qty">${this.escape(this.formatQuantity(quantity, data.unit, level))}</span>` +
              `</li>`
            );
          })
          .join('');

        let html = `<ul class="warehouse-stock__list list-unstyled" role="list">${items}</ul>`;

        if (this.dataset.showTotal === '1' && this.dataset.displayMode === 'exact') {
          html +=
            `<p class="warehouse-stock__total">` +
            `${this.escape(this.interpolate(this.strings.total, '__QTY__', this.formatNumber(data.total)))}</p>`;
        }

        if (data.stale && data.asOf) {
          const ago = this.timeAgo(data.asOf);
          if (ago) {
            html += `<p class="warehouse-stock__meta">${this.escape(this.interpolate(this.strings.updated_ago, '__TIME__', ago))}</p>`;
          }
        }

        this.body.innerHTML = html;
      }

      formatQuantity(quantity, unit, level) {
        const mode = this.dataset.displayMode;
        if (mode === 'available') {
          return quantity > 0 ? this.strings.in_stock || '' : this.strings.out_of_stock || '';
        }
        if (mode === 'banded') {
          if (quantity <= 0) return this.strings.out_of_stock || '';
          return level === 'low' ? this.strings.low_stock || '' : this.strings.in_stock || '';
        }
        const unitLabel = this.strings[`unit_${unit}`] || '';
        return `${this.formatNumber(quantity)}${unitLabel ? ` ${unitLabel}` : ''}`;
      }

      formatNumber(value) {
        const number = Number(value) || 0;
        if (Number.isInteger(number)) return String(number);
        try {
          return number.toLocaleString(this.dataset.locale || undefined, {
            maximumFractionDigits: 3,
          });
        } catch (error) {
          return String(number);
        }
      }

      timeAgo(iso) {
        const then = new Date(iso).getTime();
        if (Number.isNaN(then)) return '';
        const minutes = Math.round((Date.now() - then) / 60000);
        if (minutes < 1) return '';
        try {
          const rtf = new Intl.RelativeTimeFormat(this.dataset.locale || undefined, {
            numeric: 'always',
            style: 'narrow',
          });
          const parts =
            minutes < 60
              ? rtf.formatToParts(-minutes, 'minute')
              : rtf.formatToParts(-Math.round(minutes / 60), 'hour');

          return parts
            .filter((part) => part.type !== 'literal' || /\d/.test(part.value) === false)
            .map((part) => part.value)
            .join('')
            .replace(/\s*(ago|назад|წინ)\s*$/i, '')
            .trim();
        } catch (error) {
          return minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`;
        }
      }

      interpolate(template, token, value) {
        return String(template || '').replace(token, value);
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = String(value == null ? '' : value);
        return div.innerHTML;
      }
    }
  );
}
